import { DateTimePicker, FormInput, Modal, PrimaryButton, setDateWithTimezone } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { ResendNewsletterErrorResponseContentInterface } from 'modules/newsletters/single/types/error-response-content-interface'
import React, { useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { filterPassedTime } from 'shared/utils/date-time-format'

export interface ResendNewsletterModalWithoutButtonProps {
  opened: boolean
  subject: string
  onClose: () => void
  onResend: (data: any) => Promise<void>
}

interface ErrorsInterface {
  scheduledAt?: string
  subject?: string
  common?: string
}

const defaultErrors = {
  scheduledAt: '',
  subject: '',
  common: '',
}

const ResendNewsletterModalWithoutButton = ({
  subject,
  opened,
  onClose,
  onResend,
}: ResendNewsletterModalWithoutButtonProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const [time, setTime] = useState<string | null>(null)
  const [tempValue, setTempValue] = useState(subject)
  const [isFetching, setIsFetching] = useState(false)
  const [errors, setErrors] = useState<ErrorsInterface>(defaultErrors)

  const handleResend = async () => {
    try {
      setIsFetching(true)
      await onResend({ scheduledAt: time, subject: tempValue })
      setIsFetching(false)
      onClose()
      setErrors(defaultErrors)
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const error = e.errors as ResendNewsletterErrorResponseContentInterface
        if (e.errors.common) {
          setErrors(prev => {
            return {
              ...prev,
              common: error.common.join(''),
            }
          })
        }
        const fields = !Array.isArray(error.fields) ? error.fields : undefined
        if (fields) {
          setErrors(prev => {
            return {
              ...prev,
              subject: fields.subject?.join(''),
              scheduledAt: fields.scheduledAt?.join(''),
            }
          })
        }
      }
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('dashboard.newsletter.resend_title')}
      isFetching={isFetching}
    >
      <form className="flex flex-col gap-6 lg:gap-7">
        <div className="flex flex-col justify-center gap-3">
          <FormInput
            label={t('dashboard.newsletter.subject_title')}
            value={tempValue}
            onChange={e => setTempValue(e.target.value)}
            error={errors.subject}
          />
        </div>
        <div className="flex flex-col justify-center gap-3">
          <DateTimePicker
            value={time}
            onChange={setTime}
            label={t('dashboard.newsletter.send_at_title')}
            customProps={{
              minDate: setDateWithTimezone('', user?.timezone),
              filterTime: time => filterPassedTime(time, user?.timezone),
            }}
            error={errors.scheduledAt}
            createdAt={user?.createdAt}
            timezone={user?.timezone}
            intlLocale={intlLocale}
          />
        </div>
        <div className="flex justify-center gap-4">
          <PrimaryButton
            onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              await handleResend()
            }}
            isFetching={isFetching}
          >
            {t('dashboard.newsletter.duplicate_and_send')}
          </PrimaryButton>
        </div>
        {errors.common && (
          <p
            className="text-danger [&_a]:primary-link text-center text-sm"
            dangerouslySetInnerHTML={{ __html: errors.common }}
          />
        )}
      </form>
    </Modal>
  )
}

export default ResendNewsletterModalWithoutButton
